/**
 * @Author: Nemo
 * @Description: 表单行字段组件 - 用于将多个字段并排显示
 * @Date: 2023-11-30
 * @FilePath: /rhcn-checkout-web/src/componentsV2/busi/dynamic-form/FormRowFields.vue
 */
<template>
  <div class="form-row-fields">
    <div
      v-for="subfield in fields"
      :key="subfield.name"
      class="form-row-field"
      :class="{'highlight-error': errors[subfield.name]}"
    >
      <!-- 字段标签区域 -->
      <div class="form-field-header">
        <!-- 字段标签 -->
        <div class="field-label">
          <span class="label-text" v-if="subfield.label">{{ getSubfieldLabel(subfield) }}</span>
          <span class="label-text" v-else>&nbsp;</span>
        </div>

        <!-- 必填错误信息 - 右上角 -->
        <transition name="slide-top" appear>
          <div v-if="$parent.isFullValidation && subfield.require === 'Y' && !value[subfield.name]" class="field-error top-right">
            {{ $t('message.validator.required') }}
          </div>
        </transition>
      </div>
      <!-- 表单控件 -->
      <FormField
        :field="getFieldConfig(subfield)"
        :component-type="componentType || subfield.componentType"
        :value="value[subfield.name]"
        :error="$parent.isFullValidation && subfield.require === 'Y' && !value[subfield.name]"
        @input="onInput(subfield.name, $event)"
        @focus="onFocus(subfield.name, $event)"
        @blur="onBlur(subfield.name, $event)"
        @change="onChange(subfield.name, $event)"
        @validate="onValidate(subfield.name, $event)"
        :data-field="subfield.name"
        :ref="`subfield-${subfield.name}`"
      />

      <!-- 格式错误信息 - 下方显示 (非必填错误) -->
      <!-- <transition name="slide-bottom" appear>
        <div
          v-if="errors[subfield.name] && (value[subfield.name] || subfield.require !== 'Y')"
          class="field-error bottom-left"
        >
          {{ errors[subfield.name] }}
        </div>
      </transition> -->
    </div>
  </div>
</template>

<script>
import { createValidator } from '@/plugins/validator/index';

import FormField from './FormField.vue';

export default {
  name: 'FormRowFields',
  components: {
    FormField,
  },
  props: {
    // 行字段配置
    field: {
      type: Object,
      required: true,
    },
    // 子字段配置数组
    fields: {
      type: Array,
      required: true,
    },
    // 子字段值对象
    value: {
      type: Object,
      default: () => ({}),
    },
    // 错误信息对象
    errors: {
      type: Object,
      default: () => ({}),
    },
    // 组件类型（可选，用于覆盖字段自身的组件类型）
    componentType: {
      type: String,
      default: null,
    },
  },

  created() {
    // 初始化子字段验证器和值
    this.initSubFields();
  },

  methods: {
    // 初始化子字段验证器和值
    initSubFields() {
      // 在行字段中，需要确保子字段有验证器
      if (this.field.rowFields) {
        this.field.rowFields.forEach(subfield => {
          // 如果子字段有验证规则但父组件没有为其创建验证器
          if (subfield.validationRules && subfield.validationRules.length && !this.$parent.validators[subfield.name]) {
            // 创建验证器
            this.$parent.validators[subfield.name] = createValidator(this.$i18n);
          }

          // 如果子字段在表单数据中没有值，初始化它
          if (this.value[subfield.name] === undefined) {
            this.$set(this.value, subfield.name, subfield.value || '');
          }
        });
      }
    },

    // 获取子字段标签
    getSubfieldLabel(subfield) {
      if (subfield.label) {
        // 处理多语言键
        if (typeof subfield.label === 'string' && subfield.label.startsWith('message.')) {
          return this.$t(subfield.label);
        }
        return subfield.label;
      }
      return subfield.name;
    },

    // 获取字段配置
    getFieldConfig(field) {
      // 复制一个新字段配置，避免修改原始配置
      const newField = { ...field };
      return newField;
    },

    // 输入事件
    onInput(fieldName, value) {
      this.$emit('field-input', { name: fieldName, value });
    },

    // 聚焦事件
    onFocus(fieldName, event) {
      this.$emit('field-focus', { name: fieldName, value: this.value[fieldName] });
    },

    // 失焦事件
    onBlur(fieldName, event) {
      // 验证字段
      this.validateSubField(fieldName);

      this.$emit('field-blur', { name: fieldName, value: this.value[fieldName] });
    },

    // 验证子字段
    validateSubField(fieldName) {
      // 找到子字段配置
      const subfield = this.fields.find(f => f.name === fieldName);
      if (!subfield) return;

      // 获取验证器和值
      const validator = this.$parent.validators[fieldName];
      const value = this.value[fieldName];

      // 如果没有验证器，跳过验证
      if (!validator) return;

      // 获取验证规则
      let rules = [...(subfield.validationRules || [])];

      // 根据父组件的验证模式处理规则
      if (this.$parent.internalValidationMode === 'full') {
        // 如果字段是必填的，确保包含required规则
        if (subfield.require === 'Y' && !rules.some(rule => rule === 'required' || (typeof rule === 'object' && rule.name === 'required'))) {
          rules.unshift('required');
        }
      } else {
        // 格式验证模式，过滤掉required规则
        rules = rules.filter(rule => {
          if (typeof rule === 'string') {
            return rule !== 'required';
          } else {
            return rule.name !== 'required';
          }
        });
      }

      // 如果没有规则，无需验证
      if (!rules.length) {
        this.$delete(this.errors, fieldName);
        return { valid: true };
      }

      // 执行验证
      const result = validator.validate(value, rules);

      // 处理验证结果
      if (!result.valid) {
        this.$set(this.errors, fieldName, result.error);
      } else {
        this.$delete(this.errors, fieldName);
      }

      return result;
    },

    // 变更事件
    onChange(fieldName, event) {
      this.$emit('field-change', { name: fieldName, value: this.value[fieldName] });
    },

    // 验证事件
    onValidate(fieldName, result) {
      this.$emit('field-validate', { name: fieldName, ...result });
    },
  },
};
</script>

<style lang="scss" scoped>
.form-row-fields {
  display: flex;
  width: 100%;
  gap: 12px;

  .form-row-field {
    flex: 1;
    position: relative;

    .form-field-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
      min-height: 24px; /* 确保标签区域有固定高度 */
    }

    .field-label {
      display: flex;
      align-items: center;
      font-size: 14px;
      min-height: 20px;

      .label-text {
        color: #1A202C;
        font-weight: 500;
        font-family: "Open Sans regular";
        min-height: 20px;
        display: block;
      }
    }

    .field-error {
      font-size: 12px;
      color: #f56c6c;
      font-family: Open Sans regular;
      &.top-right {
        text-align: right;
      }

      &.bottom-left {
        display: block;
        margin-top: 6px;
        margin-bottom: 0;
        min-height: 0;
        text-align: left;
      }
    }

    &.highlight-error {
      &.highlight-active {
        animation: pulse 1.5s ease;
      }
    }
  }

  // Vue Transition动画
  .slide-top-enter-active,
  .slide-bottom-enter-active {
    animation: slideDown 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  }

  .slide-top-leave-active,
  .slide-bottom-leave-active {
    animation: slideUp 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-15px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-15px);
    }
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(#f56c6c, 0.4);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(#f56c6c, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(#f56c6c, 0);
    }
  }
}
</style>
